import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  TrendingUp,
  ArrowLeft,
  Briefcase,
  Users,
  MapPin,
  DollarSign,
  BarChart3,
  RefreshCw,
  Zap,
  Target,
  Globe,
  Building2,
  Sparkles,
  Download
} from "lucide-react";

const MarketInsights = () => {
  const { user } = useAuth();
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMarketData();
  }, []);

  const fetchMarketData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/analytics/market-data`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setMarketData(data);
      } else {
        toast.error("Failed to load market data");
      }
    } catch (error) {
      console.error("Failed to fetch market data:", error);
      toast.error("Failed to load market data");
    } finally {
      setLoading(false);
    }
  };

  const dashboardUrl = user?.role === "recruiter" ? "/dashboard/recruiter" : "/dashboard/jobseeker";

  const exportPDF = async () => {
    try {
      toast.loading("Generating PDF report...", { id: "pdf-export" });
      const response = await fetch(`${API}/reports/analytics/pdf?report_type=market`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `rekrutor-market-insights.pdf`;
        link.click();
        URL.revokeObjectURL(url);
        toast.success("PDF report downloaded!", { id: "pdf-export" });
      } else {
        toast.error("Failed to generate PDF", { id: "pdf-export" });
      }
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to generate PDF", { id: "pdf-export" });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      <Sidebar />
      
      <main className="lg:ml-64 min-h-screen p-6 pt-16 lg:pt-6">
        <div className="max-w-6xl mx-auto">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold mb-2">
                MARKET <span className="text-crimson">INSIGHTS</span>
              </h1>
              <p className="text-gray-400">Real-time job market data and trends</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={exportPDF}
                variant="outline"
                className="border-crimson text-crimson hover:bg-crimson hover:text-white"
                data-testid="export-pdf-btn"
              >
                <Download className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">PDF</span>
              </Button>
              <Button
                onClick={fetchMarketData}
                variant="outline"
                className="border-[#333] text-white"
                data-testid="refresh-market-btn"
              >
                <RefreshCw className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </div>
          </div>

          <div className="space-y-6">
            {/* Platform Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { 
                  label: "Active Jobs", 
                  value: marketData?.platform_stats?.active_jobs || 0, 
                  icon: Briefcase,
                  color: "bg-blue-500"
                },
                { 
                  label: "Total Users", 
                value: marketData?.platform_stats?.total_users || 0, 
                icon: Users,
                color: "bg-green-500"
              },
              { 
                label: "Applications", 
                value: marketData?.platform_stats?.applications_this_month || 0, 
                icon: Target,
                color: "bg-crimson"
              },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">{stat.label}</p>
                    <p className="text-3xl font-bold text-white">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 ${stat.color}/20 flex items-center justify-center`}>
                    <stat.icon className={`w-6 h-6 ${stat.color.replace('bg-', 'text-')}`} />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Trending Skills */}
          <motion.div
            className="bg-obsidian-light border border-[#222] p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-crimson" />
              <h2 className="font-heading text-lg font-bold">Trending Skills</h2>
            </div>
            <div className="flex flex-wrap gap-3">
              {marketData?.trending_skills?.map((skill, i) => (
                <motion.div
                  key={skill.skill}
                  className="px-4 py-2 bg-obsidian border border-[#333] flex items-center gap-2"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4 + i * 0.05 }}
                >
                  <span className="text-white">{skill.skill}</span>
                  <span className="text-crimson font-bold">{skill.demand}</span>
                  <span className="text-green-400 text-xs">+{skill.growth}%</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Industry Breakdown */}
            <motion.div
              className="bg-obsidian-light border border-[#222] p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-5 h-5 text-crimson" />
                <h2 className="font-heading text-lg font-bold">Industry Breakdown</h2>
              </div>
              <div className="space-y-3">
                {marketData?.industry_breakdown?.map((industry) => (
                  <div key={industry.industry}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-300">{industry.industry}</span>
                      <span className="text-white">{industry.jobs} jobs ({industry.percentage}%)</span>
                    </div>
                    <div className="h-2 bg-obsidian rounded-full overflow-hidden">
                      <div className="h-full bg-crimson" style={{ width: `${industry.percentage}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Top Locations */}
            <motion.div
              className="bg-obsidian-light border border-[#222] p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-crimson" />
                <h2 className="font-heading text-lg font-bold">Top Locations</h2>
              </div>
              <div className="space-y-3">
                {marketData?.top_locations?.map((location, i) => (
                  <div 
                    key={location.location} 
                    className="flex items-center justify-between p-3 bg-obsidian border border-[#333]"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-crimson/20 flex items-center justify-center text-sm font-bold text-crimson">
                        {i + 1}
                      </div>
                      <span className="text-white">{location.location}</span>
                    </div>
                    <span className="text-gray-400">{location.jobs} jobs</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Salary Insights */}
          <motion.div
            className="bg-obsidian-light border border-[#222] p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="w-5 h-5 text-crimson" />
              <h2 className="font-heading text-lg font-bold">Salary Insights</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-obsidian border border-[#333]">
                <p className="text-gray-400 text-sm mb-1">Low Range</p>
                <p className="text-2xl font-bold text-white">
                  ${(marketData?.salary_insights?.range_low || 0).toLocaleString()}
                </p>
              </div>
              <div className="text-center p-4 bg-crimson/10 border border-crimson/30">
                <p className="text-gray-400 text-sm mb-1">Average</p>
                <p className="text-3xl font-bold text-crimson">
                  ${(marketData?.salary_insights?.average || 0).toLocaleString()}
                </p>
              </div>
              <div className="text-center p-4 bg-obsidian border border-[#333]">
                <p className="text-gray-400 text-sm mb-1">High Range</p>
                <p className="text-2xl font-bold text-white">
                  ${(marketData?.salary_insights?.range_high || 0).toLocaleString()}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Market Trends */}
          <motion.div
            className="bg-obsidian-light border border-[#222] p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-crimson" />
              <h2 className="font-heading text-lg font-bold">Market Trends</h2>
            </div>
            <div className="space-y-3">
              {marketData?.market_trends?.map((trend, i) => (
                <div 
                  key={i} 
                  className="flex items-center gap-3 p-4 bg-obsidian border border-[#333]"
                >
                  <div className={`w-3 h-3 rounded-full ${
                    trend.sentiment === "positive" ? "bg-green-500" :
                    trend.sentiment === "negative" ? "bg-red-500" :
                    "bg-yellow-500"
                  }`} />
                  <span className="text-white">{trend.trend}</span>
                </div>
              ))}
            </div>
          </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MarketInsights;
